import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORE_KEY = "SCROLL_POS";
/** 只留最近用過的這幾筆歷史的位置，sessionStorage 才不會越堆越大 */
const MAX_ENTRIES = 60;
/** 內容是非同步載入的：頁面還不夠高時捲不到目標，最多再等這麼久 */
const RESTORE_MS = 1500;
/** 使用者自己動了就不要再把他拉回去 */
const USER_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];

function readStore() {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function writeStore(positions) {
  try {
    const keys = Object.keys(positions);
    const kept = keys.slice(Math.max(0, keys.length - MAX_ENTRIES));
    sessionStorage.setItem(STORE_KEY, JSON.stringify(kept.reduce((a, k) => ({ ...a, [k]: positions[k] }), {})));
  } catch (e) {
    /* 存不進去（無痕模式、空間滿了）只是重新整理後不記得位置，不影響操作 */
  }
}

/** 記一筆位置並移到最新：刪掉再加回去，Object.keys 的順序才代表「最近用過」，裁切時丟的是最舊的 */
function remember(positions, key, y) {
  delete positions[key];
  positions[key] = y;
}

/**
 * 換頁的捲動位置。
 *
 * BrowserRouter 換頁時完全不碰捲動：從捲到底的清單點進詳情，詳情會停在半腰；
 * 按上一頁回清單，又停在詳情那一頁的高度，得自己往回找剛剛看到哪。
 * 瀏覽器內建的還原也靠不住 —— 它在 popstate 當下就捲，SPA 的內容那時還沒畫出來。
 *
 * 規則跟一般網站一樣：
 *   - 前往新頁（PUSH，或 REPLACE 到另一個路徑）→ 捲到頂
 *   - 上一頁／下一頁（POP）→ 回到離開那一筆歷史時的位置
 *   - 同一頁只改網址參數的 REPLACE（例如篩選條件寫回網址）→ 不動
 *
 * 位置以 location.key 記，每一筆歷史各記各的；同一個網址進來兩次也不會互相蓋掉。
 */
export default function useScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();

  const positions = useRef(null);
  if (positions.current === null) positions.current = readStore();
  const keyRef = useRef(null);
  const pathRef = useRef(location.pathname);
  const cancelRestore = useRef(null);

  /*
   * 每一份新載入的頁面（網址列、書籤、外部連結、401 導回登入）第一筆歷史的 key 都是 "default"，
   * 不同頁面會共用同一格，還原時就跳到別頁的捲動位置。
   * 遇到 "default" 就換一個專屬的 key 寫回 history.state：重新整理、或從別的網站按上一頁回來時，
   * React Router 讀到的就是這個 key，找得回自己的位置；全新載入則一律從頂端開始。
   */
  const aliasRef = useRef(null);
  const keyOf = (loc) => {
    if (loc.key !== "default") return loc.key;
    if (!aliasRef.current) {
      aliasRef.current = `d${Math.random().toString(36).slice(2, 10)}`;
      try {
        window.history.replaceState({ ...(window.history.state || {}), key: aliasRef.current }, "");
      } catch (e) {
        /* 改不了 history.state 就只是這一筆重新整理後不記得位置 */
      }
    }
    return aliasRef.current;
  };

  useEffect(() => {
    const { history } = window;
    if (!("scrollRestoration" in history)) return undefined;
    const prev = history.scrollRestoration;
    history.scrollRestoration = "manual";
    return () => {
      history.scrollRestoration = prev;
    };
  }, []);

  /* 持續記下「目前這一筆歷史」捲到哪；等到要離開才量的話，新頁面已經畫上去、高度早就變了 */
  useEffect(() => {
    let raf = 0;
    const save = () => {
      raf = 0;
      if (keyRef.current) remember(positions.current, keyRef.current, window.scrollY);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(save);
    };
    const persist = () => writeStore(positions.current);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", persist);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", persist);
      if (raf) cancelAnimationFrame(raf);
      if (cancelRestore.current) cancelRestore.current();
      persist();
    };
  }, []);

  /* layout effect：趕在瀏覽器畫出新頁之前決定捲動，才不會先閃一下舊位置 */
  useLayoutEffect(() => {
    const prevKey = keyRef.current;
    const key = keyOf(location);
    keyRef.current = key;
    const pathChanged = pathRef.current !== location.pathname;
    pathRef.current = location.pathname;

    let target = null;
    // "default" 代表這份頁面是全新載入的（不是重新整理、也不是上一頁回來），沒有位置可還原
    if (navType === "POP") target = location.key === "default" ? 0 : positions.current[key] ?? 0;
    else if (navType === "PUSH" || pathChanged) target = 0;

    if (target === null) {
      // 同頁 REPLACE：舊的那一筆歷史已經被取代、再也回不去，位置搬到新 key 上，舊 key 不必佔位
      if (prevKey && prevKey !== key) delete positions.current[prevKey];
      remember(positions.current, key, window.scrollY);
      return;
    }

    if (cancelRestore.current) cancelRestore.current();
    writeStore(positions.current);

    let frame = 0;
    const started = performance.now();
    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      USER_EVENTS.forEach((ev) => window.removeEventListener(ev, stop));
      if (cancelRestore.current === stop) cancelRestore.current = null;
    };
    const tick = () => {
      window.scrollTo(0, target);
      if (Math.abs(window.scrollY - target) < 2 || performance.now() - started > RESTORE_MS) {
        stop();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    USER_EVENTS.forEach((ev) => window.addEventListener(ev, stop, { passive: true }));
    cancelRestore.current = stop;
    tick();
    // 只在換到另一筆歷史時執行；navType / pathname 會跟著 key 一起變
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
}
